import { useEffect, useState, useCallback, useRef } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  ArrowRight,
  Sparkles,
  Check,
  X,
  Coins,
  Layers,
  BookMarked,
  PartyPopper,
  Home,
  Book,
  Quote,
  Volume2,
  Loader2,
} from "lucide-react";
import api from "@/lib/api";
import ConfettiBurst from "@/components/ConfettiBurst";
import { t } from "@/i18n";

function AudioButton({ text, size = 16, className = "" }) {
  const [state, setState] = useState("idle");
  const audioRef = useRef(null);

  const play = async (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (!text || state === "loading") return;
    setState("loading");
    try {
      const res = await api.get(
        `/dict/audio?text=${encodeURIComponent(text)}`,
        {
          responseType: "blob",
        },
      );
      const url = URL.createObjectURL(res.data);
      if (audioRef.current) URL.revokeObjectURL(audioRef.current);
      audioRef.current = url;
      const audio = new Audio(url);
      audio.onended = () => setState("idle");
      audio.onerror = () => setState("error");
      await audio.play();
      setState("idle");
    } catch {
      setState("error");
    }
  };

  return (
    <button
      onClick={play}
      disabled={state === "loading"}
      title={t("dictionaryworden.listen")}
      aria-label={t("dictionaryworden.listen_to_the_pronunciation")}
      className={`shrink-0 rounded-full border border-white/12 bg-white/[0.06] hover:bg-white/12 flex items-center justify-center transition disabled:opacity-50 ${className}`}
    >
      {state === "loading" ? (
        <Loader2 size={size} className="animate-spin text-white/60" />
      ) : (
        <Volume2
          size={size}
          className={state === "error" ? "text-red-400" : "text-white/60"}
        />
      )}
    </button>
  );
}

const DIALECT_LABEL = {
  MSA: "Modern Standard Arabic",
  "darija-maroc": "Moroccan Darija",
  "darija-algerie": "Algerian Darija",
  egyptien: "Egyptian Arabic",
  levantin: "Levantine Arabic",
};
const DIALECT_ORDER = [
  "MSA",
  "darija-maroc",
  "darija-algerie",
  "egyptien",
  "levantin",
];
const dialectLabel = (d) => DIALECT_LABEL[d] || d;
const categoryLabel = (c) =>
  (c || "general").replace(/[-_]/g, " ").replace(/^\w/, (m) => m.toUpperCase());

function buildIntroSentences(w, uniqueVariants) {
  const dLabel = dialectLabel(w.dialect);
  const sentences = [];

  sentences.push(
    `In ${dLabel}, "${w.translation}" is ${w.word_ar}, pronounced "${w.word_latin}".`,
  );

  if (uniqueVariants && uniqueVariants.length > 0) {
    // Regroupe les variantes par mot arabe identique pour éviter les répétitions
    const grouped = {};
    uniqueVariants.forEach((v) => {
      if (!grouped[v.word_ar]) {
        grouped[v.word_ar] = { word_latin: v.word_latin, dialects: [] };
      }
      grouped[v.word_ar].dialects.push(dialectLabel(v.dialect));
    });

    const list = Object.entries(grouped)
      .map(([ar, data]) => {
        const dList = data.dialects;
        const dString =
          dList.length > 1
            ? dList.slice(0, -1).join(", ") + " and " + dList[dList.length - 1]
            : dList[0];
        return `${ar} (${data.word_latin}) in ${dString}`;
      })
      .join("; ");

    sentences.push(
      `The same word is also said as ${list}. These all mean "${w.translation}", but they're used in different regions.`,
    );
  } else {
    sentences.push(
      `This word belongs to the "${categoryLabel(w.category)}" category in the Harf dictionary.`,
    );
  }

  return sentences;
}

function buildRootSentences(w, rootWords) {
  if (!w.root) return [];
  const sentences = [
    `The root of ${w.word_ar} (${w.word_latin}) is ${w.root}.`,
  ];

  if (rootWords && rootWords.length > 0) {
    const list = rootWords
      .slice(0, 5)
      .map((r) => `${r.word_ar} (${r.word_latin}, "${r.translation}")`)
      .join(", ");
    sentences.push(
      `${rootWords.length} other word${rootWords.length > 1 ? "s" : ""} in the dictionary share${
        rootWords.length > 1 ? "" : "s"
      } this root: ${list}.`,
    );
    sentences.push(
      `Recognizing the root ${w.root} in an unfamiliar word helps you guess its general meaning, even if you've never seen it before.`,
    );
  } else {
    sentences.push(
      `No other word from the root ${w.root} is in the Harf dictionary yet.`,
    );
  }

  return sentences;
}

function PublicNav() {
  const nav = useNavigate();
  return (
    <header className="sticky top-0 z-40 pt-4 pb-3 bg-[#070d14]/90 backdrop-blur-xl border-b border-white/[0.06]">
      <div className="max-w-5xl mx-auto px-4 flex items-center gap-2">
        <Link to="/" className="flex items-center gap-2.5 shrink-0 mr-2">
          <div className="w-9 h-9 rounded-xl bg-white/10 border border-white/15 flex items-center justify-center font-head font-bold arabic">
            ع
          </div>
          <span className="hidden sm:block font-head font-extrabold text-lg">
            {t("navbar.harf")}
          </span>
        </Link>
        <nav className="flex items-center gap-1 rounded-full border border-white/12 bg-white/[0.05] px-1.5 py-1.5">
          <Link
            to="/"
            className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-sm font-medium text-white/70 hover:text-white hover:bg-white/10 transition"
          >
            <Home size={14} /> <span className="hidden sm:inline">{t("dictionaryworden.home")}</span>
          </Link>
          <Link
            to="/dictionary"
            className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-sm font-medium text-white/70 hover:text-white hover:bg-white/10 transition"
          >
            <Book size={14} /> {t("dictionaryworden.dictionary")}
          </Link>
        </nav>
        <div className="flex-1" />
        <button
          onClick={() => nav("/auth")}
          className="px-5 py-2.5 rounded-full bg-white/12 border border-white/20 backdrop-blur-xl text-sm font-semibold hover:bg-white/20 transition shrink-0"
        >
          {t("dictionaryworden.get_started")}
        </button>
      </div>
    </header>
  );
}

function WordQuiz({ dialect, slug }) {
  const nav = useNavigate();
  const [questions, setQuestions] = useState(null);
  const [idx, setIdx] = useState(0);
  const [picked, setPicked] = useState(null);
  const [earned, setEarned] = useState(0);
  const [burst, setBurst] = useState(0);
  const [finished, setFinished] = useState(false);

  useEffect(() => {
    setQuestions(null);
    setIdx(0);
    setPicked(null);
    setEarned(0);
    setFinished(false);
    api
      .get(`/dict/word/${dialect}/${slug}/quiz-set?lang=en`)
      .then((r) => setQuestions(r.data.questions || []))
      .catch(() => setQuestions([]));
  }, [dialect, slug]);

  const choose = (oi) => {
    if (picked !== null || !questions) return;
    setPicked(oi);
    const q = questions[idx];
    if (oi === q.answer) {
      setEarned((e) => e + 1);
      setBurst((b) => b + 1);
    }
  };

  const next = () => {
    if (!questions) return;
    if (idx + 1 >= questions.length) setFinished(true);
    else {
      setIdx(idx + 1);
      setPicked(null);
    }
  };

  if (questions === null) {
    return (
      <div className="rounded-3xl border border-white/[0.08] bg-white/[0.03] p-8 text-center text-white/40 text-sm">
        {t("dictionaryworden.preparing_the_quiz")}
      </div>
    );
  }
  if (questions.length === 0) return null;

  if (finished) {
    return (
      <div className="rounded-3xl border border-amber-400/25 bg-amber-400/[0.06] p-8 text-center relative overflow-hidden">
        <PartyPopper size={36} className="mx-auto text-amber-300 mb-3" />
        <h3 className="font-head text-xl font-bold mb-1">
          {earned} / {questions.length} correct
        </h3>
        <p className="text-white/55 text-sm mb-6">
          {t("dictionaryworden.these_are_preview_coins_create_an")}
        </p>
        <button
          onClick={() => nav("/auth")}
          className="px-7 py-3.5 rounded-full bg-white text-[#070d14] font-bold hover:bg-white/90 transition inline-flex items-center gap-2"
        >
          {t("dictionaryworden.keep_my")} {earned} coin{earned > 1 ? "s" : ""} <ArrowRight size={17} />
        </button>
      </div>
    );
  }

  const q = questions[idx];
  const isArabicOptions = q.mode === "meaning_to_arabic";

  return (
    <div className="rounded-3xl border border-white/[0.08] bg-white/[0.03] p-6 relative overflow-hidden">
      {burst > 0 && <ConfettiBurst key={burst} count={40} />}
      <div className="flex items-center justify-between mb-5">
        <span className="text-xs font-semibold text-white/40">
          {t("exerciseblock.question")} {idx + 1} / {questions.length}
        </span>
        <span className="flex items-center gap-1 text-xs font-bold text-amber-300">
          <Coins size={13} /> {earned}
        </span>
      </div>

      <p
        className={`font-semibold mb-5 break-words ${
          q.mode === "arabic_to_meaning" ? "arabic text-2xl" : "text-[15px]"
        }`}
        dir={q.mode === "arabic_to_meaning" ? "rtl" : "ltr"}
      >
        {q.prompt}
      </p>

      <div className="grid gap-2.5 mb-2">
        {q.options.map((opt, oi) => {
          const isAns = oi === q.answer;
          const show = picked !== null;
          let cls =
            "border-white/12 bg-white/[0.03] hover:border-white/25 hover:bg-white/[0.06]";
          if (show && isAns) cls = "border-emerald-400/60 bg-emerald-400/10";
          else if (show && oi === picked)
            cls = "border-red-400/50 bg-red-400/10";
          return (
            <button
              key={oi}
              disabled={show}
              onClick={() => choose(oi)}
              className={`px-4 py-3 rounded-2xl border text-left text-[15px] transition flex items-center justify-between gap-2 min-w-0 ${cls} ${
                isArabicOptions ? "arabic text-xl" : ""
              }`}
              dir={isArabicOptions ? "rtl" : "ltr"}
            >
              <span className="break-words">{opt}</span>
              {show && isAns && (
                <Check size={16} className="text-emerald-400 shrink-0" />
              )}
              {show && oi === picked && !isAns && (
                <X size={16} className="text-red-400 shrink-0" />
              )}
            </button>
          );
        })}
      </div>

      {picked !== null && (
        <div className="mt-4">
          {picked === q.answer && (
            <p className="text-center text-xs font-bold text-amber-300 mb-3">
              {t("dictionaryworden.1_coin")}
            </p>
          )}
          <button
            onClick={next}
            className="w-full py-3 rounded-2xl bg-white text-[#070d14] font-bold hover:bg-white/90 transition"
          >
            {idx + 1 >= questions.length ? t("dictionaryworden.see_my_score") : t("dictionaryworden.continue")}
          </button>
        </div>
      )}
    </div>
  );
}

export default function DictionaryWordEn() {
  const { dialect, slug } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [related, setRelated] = useState([]);
  const [rootWords, setRootWords] = useState([]);

  const load = useCallback(() => {
    setData(null);
    setNotFound(false);
    setLoadError(false);
    api
      .get(`/dict/word/${dialect}/${slug}?lang=en`)
      .then((r) => setData(r.data))
      .catch((err) => {
        if (err?.response?.status === 404) setNotFound(true);
        else setLoadError(true);
      });
  }, [dialect, slug]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!data?.word) return;
    api
      .get(
        `/dict/words?category=${encodeURIComponent(data.word.category || "general")}&page_size=7&lang=en`,
      )
      .then((r) =>
        setRelated(
          (r.data.items || []).filter((w) => w.slug !== slug).slice(0, 6),
        ),
      )
      .catch(() => setRelated([]));
  }, [data, slug]);

  useEffect(() => {
    if (!data?.word?.root) {
      setRootWords([]);
      return;
    }
    api
      .get(
        `/dict/words?root=${encodeURIComponent(data.word.root)}&page_size=8&lang=en`,
      )
      .then((r) => {
        const items = r.data.items || [];
        // Dédoublonnage des mots de même racine par word_ar
        const uniqueRoots = [];
        const seenRoots = new Set();
        items
          .filter((w) => w.slug !== slug)
          .forEach((w) => {
            if (!seenRoots.has(w.word_ar)) {
              seenRoots.add(w.word_ar);
              uniqueRoots.push(w);
            }
          });
        setRootWords(uniqueRoots);
      })
      .catch(() => setRootWords([]));
  }, [data, slug]);

  useEffect(() => {
    if (!data?.word) return;
    const w = data.word;
    const dLabel = dialectLabel(w.dialect);
    const title = `${w.word_latin || w.word_ar}: How to Say "${w.translation}" in ${dLabel} | Harf`;
    const description = `${w.word_ar} (${w.word_latin}) means "${w.translation}" in ${dLabel}. Pronunciation, example sentence, and root on the free Harf Arabic dictionary.`;
    document.title = title;

    const setMeta = (attr, key, content) => {
      let tag = document.head.querySelector(`meta[${attr}="${key}"]`);
      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute(attr, key);
        document.head.appendChild(tag);
      }
      tag.setAttribute("content", content);
    };
    setMeta("name", "description", description);
    setMeta("property", "og:title", title);
    setMeta("property", "og:description", description);
    setMeta("property", "og:type", "article");

    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute(
      "href",
      `${window.location.origin}/dictionary/${w.dialect}/${w.slug}`,
    );

    const setAlternate = (id, hreflang, href) => {
      let tag = document.head.querySelector(`link[data-hreflang-id="${id}"]`);
      if (!tag) {
        tag = document.createElement("link");
        tag.setAttribute("rel", "alternate");
        tag.setAttribute("data-hreflang-id", id);
        document.head.appendChild(tag);
      }
      tag.setAttribute("hreflang", hreflang);
      tag.setAttribute("href", href);
    };
    setAlternate(
      "fr",
      "fr",
      `${window.location.origin}/fr/dictionary/${w.dialect}/${w.slug}`,
    );
    setAlternate(
      "en",
      "en",
      `${window.location.origin}/dictionary/${w.dialect}/${w.slug}`,
    );

    const variantText =
      (data.variants || []).length > 0
        ? ` Regional variants: ${(data.variants || [])
            .map((v) => `${v.word_ar} (${dialectLabel(v.dialect)})`)
            .join(", ")}.`
        : "";
    const ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.id = "harf-word-jsonld-en";
    ld.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "DefinedTerm",
      name: w.word_ar,
      alternateName: w.word_latin,
      description:
        `${w.word_ar}, pronounced "${w.word_latin}", means "${w.translation}" ` +
        `in ${dLabel}.` +
        (w.root ? ` Root: ${w.root}.` : "") +
        variantText,
      termCode: w.root || undefined,
      inDefinedTermSet: {
        "@type": "DefinedTermSet",
        name: "Harf Dictionary",
        url: `${window.location.origin}/dictionary`,
      },
      inLanguage: "ar",
      url: `${window.location.origin}/dictionary/${w.dialect}/${w.slug}`,
    });
    document.head.appendChild(ld);

    return () => {
      const old = document.getElementById("harf-word-jsonld-en");
      if (old) old.remove();
      document
        .querySelectorAll("link[data-hreflang-id]")
        .forEach((t) => t.remove());
    };
  }, [data]);

  if (notFound) {
    return (
      <div className="min-h-screen bg-[#070d14] text-white">
        {!user && <PublicNav />}
        <div className="flex flex-col items-center justify-center px-4 text-center py-24">
          <p className="text-white/60 mb-6">
            {t("dictionaryworden.this_word_doesn_t_exist_or")}
          </p>
          <Link
            to="/dictionary"
            className="px-6 py-3 rounded-full bg-white text-[#070d14] font-semibold"
          >
            {t("dictionaryworden.back_to_the_dictionary")}
          </Link>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="min-h-screen bg-[#070d14] text-white">
        {!user && <PublicNav />}
        <div className="max-w-md mx-auto px-4 text-center py-24">
          <p className="text-white/60 mb-3">{t("dictionaryworden.this_word_couldn_t_be_loaded")}</p>
          <p className="text-sm text-white/40">
            {t("dictionaryworden.check")}{" "}
            <code className="px-1.5 py-0.5 rounded bg-white/10">
              /api/dict/_health
            </code>
            .
          </p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-[#070d14] text-white">
        {!user && <PublicNav />}
        <div className="flex items-center justify-center py-24">
          <span className="text-white/40 text-sm">{t("dictionaryworden.loading")}</span>
        </div>
      </div>
    );
  }

  const w = data.word;

  // 1. Filtrer les doublons purs de l'API (même dialecte ET même mot)
  const uniqueVariants = [];
  const seenV = new Set();
  (data.variants || []).forEach((v) => {
    const k = `${v.dialect}-${v.word_ar}`;
    if (
      !seenV.has(k) &&
      !(v.dialect === w.dialect && v.word_ar === w.word_ar)
    ) {
      seenV.add(k);
      uniqueVariants.push(v);
    }
  });

  // 2. Combiner proprement pour les cartes
  const allForms = [
    {
      dialect: w.dialect,
      word_ar: w.word_ar,
      word_latin: w.word_latin,
      slug: w.slug,
    },
    ...uniqueVariants,
  ].sort(
    (a, b) =>
      DIALECT_ORDER.indexOf(a.dialect) - DIALECT_ORDER.indexOf(b.dialect),
  );

  const introSentences = buildIntroSentences(w, uniqueVariants);
  const rootSentences = buildRootSentences(w, rootWords);

  return (
    <div className="min-h-screen bg-[#070d14] text-white">
      {!user && <PublicNav />}

      <main className="max-w-5xl mx-auto px-4 py-10">
        <div className="grid lg:grid-cols-[1fr_360px] gap-6 items-start">
          <div className="space-y-6 min-w-0">
            <div className="rounded-[2rem] border border-white/[0.1] bg-white/[0.02] p-7 sm:p-9">
              <div className="text-center mb-6">
                <div className="text-[11px] uppercase tracking-wider text-white/35 mb-2">
                  {t("dictionaryworden.our_word")}
                </div>
                <div className="font-head text-3xl sm:text-4xl font-extrabold break-words px-2">
                  {w.translation}
                </div>
              </div>

              <div className="flex items-center gap-3 mb-6" aria-hidden="true">
                <div className="flex-1 h-px bg-white/[0.08]" />
                <span className="text-2xl font-light text-white/25 shrink-0">
                  =
                </span>
                <div className="flex-1 h-px bg-white/[0.08]" />
              </div>

              <div className="grid sm:grid-cols-2 gap-2.5">
                {allForms.map((f) => (
                  <div
                    key={`${f.dialect}-${f.slug || f.word_ar}`}
                    className={`min-w-0 rounded-2xl border p-4 text-center ${
                      f.dialect === w.dialect
                        ? "border-amber-400/30 bg-amber-400/[0.06]"
                        : "border-white/[0.08] bg-white/[0.03]"
                    }`}
                  >
                    <div className="text-[11px] uppercase tracking-wide text-white/35 mb-1.5">
                      {dialectLabel(f.dialect)}
                    </div>
                    <div
                      className="arabic text-2xl font-bold mb-1 break-words"
                      dir="rtl"
                    >
                      {f.word_ar}
                    </div>
                    <div className="flex items-center justify-center gap-2">
                      <div className="text-xs text-white/45 italic break-words">
                        {f.word_latin}
                      </div>
                      <AudioButton
                        text={f.word_ar}
                        size={12}
                        className="w-6 h-6"
                      />
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-7 pt-6 border-t border-white/[0.07] space-y-2.5">
                {introSentences.map((s, i) => (
                  <p
                    key={i}
                    className="text-[15px] text-white/60 leading-relaxed"
                  >
                    {s}
                  </p>
                ))}
                <div className="flex flex-wrap items-center gap-2 pt-2">
                  <span className="text-[11px] font-bold uppercase tracking-wider text-white/40 rounded-full border border-white/12 px-3 py-1">
                    {categoryLabel(w.category)}
                  </span>
                </div>
              </div>
            </div>

            <div className="rounded-3xl border border-white/[0.08] bg-white/[0.03] p-6 sm:p-7">
              <div className="flex items-center gap-2 mb-3">
                <Layers size={17} className="text-white/50" />
                <h2 className="font-head text-lg font-bold">
                  {w.root
                    ? `The root ${w.root} and its word family`
                    : t("dictionaryworden.the_root_of_this_word")}
                </h2>
              </div>

              <p className="text-[15px] text-white/55 leading-relaxed mb-4">
                {t("dictionaryworden.almost_every_arabic_word_is_built")}
              </p>

              {rootSentences.map((s, i) => (
                <p
                  key={i}
                  className="text-[15px] text-white/60 leading-relaxed mb-3"
                >
                  {s}
                </p>
              ))}

              {w.root ? (
                <>
                  <div className="inline-flex items-center gap-3 rounded-2xl border border-white/[0.08] bg-white/[0.04] px-5 py-3 my-4">
                    <span className="text-[11px] text-white/35 uppercase tracking-wide">
                      {t("dictionaryworden.root")}
                    </span>
                    <span className="arabic text-2xl" dir="rtl">
                      {w.root}
                    </span>
                  </div>

                  {rootWords.length > 0 && (
                    <div>
                      <h3 className="text-sm font-semibold mb-3">
                        {t("dictionaryworden.words_from_the_root")} {w.root}
                      </h3>
                      <div className="grid gap-2">
                        {rootWords.map((rw) => (
                          <Link
                            key={rw.id}
                            to={`/dictionary/${rw.dialect}/${rw.slug}`}
                            className="flex items-center justify-between gap-3 rounded-2xl border border-white/[0.06] bg-white/[0.02] px-4 py-3 hover:bg-white/[0.05] hover:border-white/15 transition"
                          >
                            <div className="flex items-baseline gap-3 min-w-0">
                              <span
                                className="arabic text-xl shrink-0"
                                dir="rtl"
                              >
                                {rw.word_ar}
                              </span>
                              <span className="text-xs text-white/40 italic shrink-0">
                                {rw.word_latin}
                              </span>
                              <span className="text-sm text-white/60 truncate">
                                {rw.translation}
                              </span>
                            </div>
                            <span className="text-[10px] text-white/30 shrink-0">
                              {dialectLabel(rw.dialect)}
                            </span>
                          </Link>
                        ))}
                      </div>
                      <p className="text-[13px] text-white/40 leading-relaxed mt-4">
                        {t("dictionaryworden.these_words_don_t_share_the")} {w.root}. This is the
                        mechanism that lets a few hundred roots unlock thousands
                        of words.
                      </p>
                    </div>
                  )}
                </>
              ) : (
                <p className="text-[13px] text-white/30">
                  {t("dictionaryworden.the_root_of_this_word_hasn")}
                </p>
              )}
            </div>

            {w.example_ar && (
              <div className="rounded-3xl border border-white/[0.08] bg-white/[0.03] p-6 sm:p-7">
                <div className="flex items-center justify-between gap-2 mb-4">
                  <div className="flex items-center gap-2">
                    <Quote size={16} className="text-white/40" />
                    <h2 className="font-head text-lg font-bold">
                      {t("dictionaryworden.example_sentence")}
                    </h2>
                  </div>
                  <AudioButton
                    text={w.example_ar}
                    size={15}
                    className="w-8 h-8"
                  />
                </div>
                <div className="arabic text-xl mb-2 text-right" dir="rtl">
                  {w.example_ar}
                </div>
                {w.example_latin && (
                  <div className="text-sm text-white/40 italic text-right mb-1">
                    {w.example_latin}
                  </div>
                )}
                {w.example && (
                  <div className="text-sm text-white/50 text-right">
                    {w.example}
                  </div>
                )}
              </div>
            )}

            {related.length > 0 && (
              <div className="rounded-3xl border border-white/[0.08] bg-white/[0.03] p-6 sm:p-7">
                <div className="flex items-center gap-2 mb-4">
                  <BookMarked size={16} className="text-white/40" />
                  <h2 className="font-head text-lg font-bold">
                    {t("dictionaryworden.more_words_in")}{categoryLabel(w.category)}"
                  </h2>
                </div>
                <div className="grid sm:grid-cols-2 gap-2">
                  {related.map((r) => (
                    <Link
                      key={r.id}
                      to={`/dictionary/${r.dialect}/${r.slug}`}
                      className="flex items-center justify-between gap-3 rounded-2xl border border-white/[0.06] bg-white/[0.02] px-4 py-3 hover:bg-white/[0.05] hover:border-white/15 transition"
                    >
                      <div className="flex items-baseline gap-2.5 min-w-0">
                        <span className="arabic text-lg shrink-0" dir="rtl">
                          {r.word_ar}
                        </span>
                        <span className="text-sm text-white/50 truncate">
                          {r.translation}
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="lg:sticky lg:top-24">
            <div className="flex items-center gap-2 mb-4 px-1">
              <Sparkles size={16} className="text-amber-300" />
              <h2 className="font-semibold text-sm">
                {t("dictionaryworden.test_your_memory_on_this_word")}
              </h2>
            </div>
            <WordQuiz dialect={w.dialect} slug={w.slug} />
          </div>
        </div>

        <button
          onClick={() => nav("/auth")}
          className="w-full mt-8 py-5 rounded-[1.75rem] bg-white text-[#070d14] font-bold text-lg hover:bg-white/90 transition inline-flex items-center justify-center gap-2"
        >
          {t("dictionaryworden.learn_arabic_earn_coins_build_your")} <ArrowRight size={19} />
        </button>

        <div className="mt-10 rounded-[2rem] border border-white/[0.1] bg-white/[0.02] p-8 sm:p-10 text-center">
          <Sparkles size={26} className="mx-auto text-amber-300 mb-4" />
          <h2 className="font-head text-xl sm:text-2xl font-bold mb-3">
            {t("dictionaryworden.what_is_harf")}
          </h2>
          <p className="text-white/55 max-w-lg mx-auto mb-3 leading-relaxed">
            {t("dictionaryworden.harf_is_an_app_for_learning")}
          </p>
          <p className="text-white/55 max-w-lg mx-auto mb-7 leading-relaxed">
            {t("dictionaryworden.this_dictionary_is_free_no_account")}
          </p>
          <button
            onClick={() => nav("/auth")}
            className="px-7 py-3.5 rounded-full bg-white text-[#070d14] font-bold hover:bg-white/90 transition inline-flex items-center gap-2"
          >
            {t("dictionaryworden.create_a_free_account")} <ArrowRight size={17} />
          </button>
        </div>
      </main>
    </div>
  );
}
